import type {
  CockpitMetricView,
  CockpitSectionViewState,
} from './cockpitViewModel';
import { limitationLabel, usageObservationLabel } from './semanticLabels';

export interface CockpitContextNotice {
  tone: 'warning' | 'error';
  summary: string;
  impact: string | null;
  action: string | null;
}

const trustedUsageStates = new Set<CockpitMetricView['usageObservationState']>([
  null,
  'confirmed',
  'late-evidence',
  'manual-reconciliation',
]);

export function resolveMetricContextNotice(
  metric: CockpitMetricView,
  sectionStale: boolean,
): CockpitContextNotice | null {
  const effectivelyStale = metric.primary && (metric.stale || sectionStale);
  const observationNeedsAttention = !trustedUsageStates.has(
    metric.usageObservationState,
  );
  const statusNeedsAttention = [
    'empty',
    'partial',
    'unavailable',
    'failed',
  ].includes(metric.status);

  if (
    !statusNeedsAttention &&
    !effectivelyStale &&
    !observationNeedsAttention
  ) {
    return null;
  }

  const fallbackSummary = metricStatusSummary(metric, effectivelyStale);
  return {
    tone: metric.status === 'failed' ? 'error' : 'warning',
    summary: metric.unavailableReason ?? fallbackSummary,
    impact: distinctGuidance(metric.impact, metric.unavailableReason),
    action: metric.action,
  };
}

export function resolveEvidenceContextNotice(
  sectionState: CockpitSectionViewState,
  relationCount: number,
): CockpitContextNotice | null {
  if (sectionState.status === 'ready' && sectionState.coverage === 'complete') {
    return relationCount === 0
      ? {
          tone: 'warning',
          summary: 'Nenhuma relação real foi observada nesta revisão.',
          impact: 'Os itens permanecem consultáveis sem inferir vínculos.',
          action: null,
        }
      : null;
  }

  const limitation = sectionState.limitations[0];
  return {
    tone: sectionState.status === 'failed' ? 'error' : 'warning',
    summary: evidenceStatusSummary(sectionState),
    impact: limitation ? limitationLabel(limitation) : null,
    action: 'Consulte os itens sem presumir vínculos ausentes.',
  };
}

function metricStatusSummary(
  metric: CockpitMetricView,
  effectivelyStale: boolean,
): string {
  if (metric.status === 'empty') {
    return 'Nenhuma evidência elegível foi observada para esta métrica.';
  }
  if (metric.status === 'partial') {
    return 'Esta métrica tem cobertura parcial.';
  }
  if (metric.status === 'unavailable') {
    return 'Esta métrica está indisponível.';
  }
  if (metric.status === 'failed') {
    return 'Falha ao atualizar esta métrica.';
  }
  if (effectivelyStale) {
    return 'Esta métrica pode estar desatualizada.';
  }
  if (metric.usageObservationState) {
    return `${usageObservationLabel(metric.usageObservationState)}.`;
  }
  return 'Esta métrica exige atenção.';
}

function distinctGuidance(
  guidance: string | null,
  summary: string | null,
): string | null {
  return guidance && guidance !== summary ? guidance : null;
}

function evidenceStatusSummary(sectionState: CockpitSectionViewState): string {
  if (sectionState.status === 'empty') {
    return 'Nenhuma relação está disponível nesta revisão.';
  }
  if (sectionState.status === 'unavailable') {
    return 'As relações estão indisponíveis nesta revisão.';
  }
  if (sectionState.status === 'failed') {
    return 'Falha ao carregar as relações desta revisão.';
  }
  return 'A cobertura das relações é parcial.';
}
